import { useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import {
  type CommunicationLanguage,
  type EmailTemplateKind,
  EmailTemplatePreviewDocument,
} from "@/graphql/generated/operations";
import { communicationLanguageName } from "@/lib/communication-language";
import { userErrorMessage } from "@/lib/user-error";

// The preview of a language variant, rendered by genesis with the code that sends the mail
// (@external-adr GENESIS-0097-a-mail-is-written-in-the-language-resolved-for-its-recipient): subject and plain text
// exactly as SMTP carries them, with sample values for listing, reference and link. Unlike the invitation's preview it
// is not an approximation. A draft that fails the local rules is not sent for rendering; the editor names the problem.
export function EmailTemplateLanguagePreview({
  kind,
  language,
  subject,
  body,
  invalid,
}: {
  kind: EmailTemplateKind;
  language: CommunicationLanguage;
  subject: string | null;
  body: string | null;
  invalid: string | null;
}) {
  const intl = useIntl();
  const { data, loading, error } = useQuery(EmailTemplatePreviewDocument, {
    variables: { input: { kind, language, subject, body } },
    skip: invalid !== null,
  });
  const preview = data?.emailTemplatePreview;
  const label = intl.formatMessage({ id: "adminSettings.emailTemplates.previewLabel", defaultMessage: "Email preview" });

  return (
    <div className="box-flat overflow-hidden bg-page" role="region" aria-label={label}>
      <div className="border-b border-ink/10 px-5 py-4">
        <div className="smycles-field-label">
          {intl.formatMessage({ id: "adminSettings.emailTemplates.subject", defaultMessage: "Subject" })}
        </div>
        <div className="mt-1 text-smycles-body font-medium text-ink">{invalid ? null : preview?.subject}</div>
      </div>
      <div className="border-b border-ink/10 px-5 py-3">
        <p className="smycles-field-meta text-ink-muted">
          {intl.formatMessage(
            {
              id: "adminSettings.emailTemplates.languagePreviewHint",
              defaultMessage:
                "This is how the email is sent in {language}. Listing, reference and link are sample values.",
            },
            { language: communicationLanguageName(intl, language) },
          )}
        </p>
      </div>
      <div className="mx-auto max-w-[42rem] px-5 py-6">
        {invalid ? (
          <p role="alert" className="smycles-field-meta text-ink">
            {invalid}
          </p>
        ) : error ? (
          <p role="alert" className="smycles-field-meta text-ink">
            {userErrorMessage(intl, error)}
          </p>
        ) : loading && !preview ? (
          <div className="smycles-field-meta text-ink-muted">
            {intl.formatMessage({ id: "adminSettings.emailTemplates.loading", defaultMessage: "Loading…" })}
          </div>
        ) : (
          <p className="whitespace-pre-line text-smycles-meta leading-6 text-ink">{preview?.body}</p>
        )}
      </div>
    </div>
  );
}
